import "@cfg/configurator-ui/skin.css";
import "./styles.css";
import { createApiClient } from "@cfg/api-contract";
import { localizeBrandConfig, type Brand } from "@cfg/brand-sdk";
import { LocaleProvider, type LocaleValue } from "@cfg/configurator-ui";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { StrictMode, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { Analytics } from "./analytics.ts";
import { App } from "./app.tsx";
import { AppCtx, type AppServices } from "./app-context.tsx";
import { Auth } from "./auth.ts";
import { makeCopy, resolveBrand } from "./brand.ts";
import { applyDocumentLocale, initialLocale, loadDesignTranslator, storeLocale } from "./locale.ts";
import { ApiRegistry, LocalRegistry } from "./registry.ts";

type Services = Omit<AppServices, "brand" | "copy" | "locale" | "setLocale">;

/** Holds the active locale and derives everything language-dependent from it. */
function Root({ brand, services }: { brand: Brand; services: Services }) {
  const [locale, setLocaleState] = useState(() => initialLocale(brand.config));
  const setLocale = useCallback((next: string) => {
    storeLocale(next);
    setLocaleState(next);
  }, []);
  const designs = useQuery({
    queryKey: ["design-text", locale],
    queryFn: () => loadDesignTranslator(services.registry, locale),
    staleTime: Infinity,
    retry: false,
  });
  const app = useMemo<AppServices>(() => {
    const config = localizeBrandConfig(brand.config, locale);
    return { ...services, brand: { ...brand, config }, copy: makeCopy(brand.config, locale), locale, setLocale };
  }, [brand, services, locale, setLocale]);
  const localeValue = useMemo<LocaleValue>(() => ({ locale, copy: app.copy, designs: designs.data ?? undefined }), [locale, app.copy, designs.data]);
  useEffect(() => applyDocumentLocale(locale, app.brand.config.meta?.description ?? app.brand.config.tagline), [locale, app.brand.config]);

  return (
    <AppCtx.Provider value={app}>
      <LocaleProvider value={localeValue}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </LocaleProvider>
    </AppCtx.Provider>
  );
}

async function boot() {
  const brand = await resolveBrand();
  const auth = new Auth(brand.config);
  await auth.init();

  const apiBase = brand.config.apiBaseUrl;
  const api = apiBase ? createApiClient({ baseUrl: apiBase, brand: brand.config.id, getToken: () => auth.getToken() }) : null;
  const registryUrl = import.meta.env.VITE_REGISTRY_URL;
  const registry = api && !registryUrl ? new ApiRegistry(api, brand.config) : new LocalRegistry(registryUrl ?? "/packs/registry.json", brand.config);

  const services: Services = {
    registry,
    api,
    auth,
    analytics: new Analytics(api, brand.config.features.analytics, apiBase),
  };
  const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 60_000, retry: 1 } } });

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Root brand={brand} services={services} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

boot().catch((err) => {
  console.error(err);
  document.getElementById("root")!.innerHTML = `<pre style="padding:2rem;color:#c0392b">Failed to start: ${String(err)}</pre>`;
});
